import { useEffect, useRef, useState } from 'react';

import type { Rect } from './widgetPosition';

/**
 * CORE-1120 — реестр зон экрана, занятых интерфейсом.
 *
 * Плавающая Agapella смонтирована в `App.tsx` рядом с `NavigationContainer` и
 * про содержимое страницы не знает ничего. До сих пор она и не могла знать:
 * геометрия оперировала только краями окна, а CORE-731 добавил полосу вдоль
 * нижнего края на двух маршрутах. Поэтому перетащенная на середину иконка
 * оставалась легальной по всем правилам и стояла поверх заголовка.
 *
 * Здесь страница получает способ сказать «этот прямоугольник занят». Реестр
 * живёт вне React-дерева по той же причине, что и `navigationRef`: читатель —
 * сосед навигатора, а писатели разбросаны по экранам, и общего родителя у них
 * нет.
 *
 * Зона — это про ПЕРЕКРЫТИЕ, а не про важность. Регистрировать стоит то, что
 * нельзя закрыть кружком 60×60: заголовки, вкладки, CTA, поле ввода, нижние
 * панели. Регистрировать всю страницу бессмысленно — иконке некуда деться, и
 * она останется на месте (см. `avoidReservedZones`).
 */

/**
 * Имя зоны шапки. Живёт здесь, а не в `Header.tsx`, чтобы потребитель отступа
 * не тянул за собой весь компонент шапки ради одной строки.
 */
export const HEADER_ZONE_ID = 'app-header';

/**
 * Имя зоны нижней панели вкладок (натив).
 *
 * CORE-1376 — «Профиль» не открывался: круг лончера в углу по умолчанию лежал
 * ровно на правой вкладке и забирал её нажатие себе. Панель рисует каждый экран
 * сам, снаружи её высоты не видно — поэтому она ОБЪЯВЛЯЕТ измеренный
 * прямоугольник, как это делает шапка, а плавающая Agapella держится выше него
 * (см. `AgapellaWidget.native`).
 */
export const TAB_BAR_ZONE_ID = 'bottom-tab-bar';

type Listener = () => void;

/**
 * Владелец нужен из-за перехода между экранами: шапку рендерит каждый экран
 * сам, и на время анимации смонтированы обе. Уходящая размонтируется ПОСЛЕ
 * того, как пришедшая объявила свой прямоугольник, — и без владельца стёрла бы
 * чужую запись, оставив страницу без зоны до следующего изменения размера.
 */
interface Entry { rect: Rect; owner: object }

const zones = new Map<string, Entry>();
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Объявить зону занятой. Повторный вызов с тем же `id` заменяет прямоугольник —
 * так высота баннера или композера может меняться на лету.
 */
export function setReservedZone(id: string, rect: Rect | null, owner: object = DEFAULT_OWNER): void {
  const previous = zones.get(id);
  if (rect == null) {
    // Снять может только тот, кто объявил: см. Entry.
    if (previous === undefined || previous.owner !== owner) return;
    zones.delete(id);
    emit();
    return;
  }
  const p = previous?.rect;
  if (previous && previous.owner === owner
    && p!.x === rect.x && p!.y === rect.y
    && p!.width === rect.width && p!.height === rect.height) {
    return; // ничего не изменилось — не будим подписчиков
  }
  zones.set(id, { rect, owner });
  emit();
}

const DEFAULT_OWNER = {};

/** Снять зону. Экран обязан звать это при размонтировании. */
export function clearReservedZone(id: string, owner: object = DEFAULT_OWNER): void {
  setReservedZone(id, null, owner);
}

/** Текущий снимок. Массив новый на каждый вызов — читатели его не мутируют. */
export function getReservedZones(): Rect[] {
  return Array.from(zones.values(), (entry) => entry.rect);
}

/**
 * Снимок без записей одного владельца.
 *
 * CORE-1120 — нужен тому, кто считает свои зоны САМ и хочет знать, что на
 * экране кроме них. Живой замер под иконкой (`useInteractiveOverlap`) щупает не
 * только базовую точку, но и место, куда иконка после этого уедет, — а чтобы
 * посчитать, куда она уедет, ему нужен тот же вход, что и геометрии: чужие
 * зоны плюс собственная находка этого круга. Свои прошлые записи в этот вход
 * попадать не должны: они уже устарели, круг их как раз и пересчитывает.
 */
export function getReservedZonesExcept(owner: object): Rect[] {
  const out: Rect[] = [];
  for (const entry of zones.values()) if (entry.owner !== owner) out.push(entry.rect);
  return out;
}

/** Одна зона по имени — для тех, кто не обходит её, а подстраивает под неё вёрстку. */
export function getReservedZone(id: string): Rect | null {
  return zones.get(id)?.rect ?? null;
}

/** Тест-хелпер: реестр — модульное состояние и между тестами не сбрасывается. */
export function resetReservedZones(): void {
  if (zones.size === 0) return;
  zones.clear();
  emit();
}

export function subscribeToReservedZones(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Живой список зон для того, кто их обходит.
 *
 * Пересчёта по resize/маршруту здесь нет намеренно: позиция иконки выводится из
 * зон на каждом рендере, а сами зоны переобъявляет тот, кто их измерил, — по
 * своему `onLayout`. Так у каждой стороны ровно одна обязанность.
 */
export function useReservedZones(): Rect[] {
  const [snapshot, setSnapshot] = useState<Rect[]>(getReservedZones);
  useEffect(() => subscribeToReservedZones(() => setSnapshot(getReservedZones())), []);
  return snapshot;
}

/**
 * Объявить зону на время жизни компонента и снять её при размонтировании.
 *
 * `rect` обычно приходит из `onLayout`; `null` означает «сейчас нечего занимать»
 * — свёрнутый баннер, скрытая панель.
 */
export function useReservedZone(id: string, rect: Rect | null): void {
  // Метка этого экземпляра хука — по ней реестр отличает «снимаю своё» от
  // «стираю чужое» на перекрёстном переходе между экранами.
  const owner = useRef({}).current;

  useEffect(() => {
    setReservedZone(id, rect, owner);
    // Зависимости — ПОЛЯ прямоугольника, а не он сам: `onLayout` отдаёт новый
    // объект на каждый замер, и по ссылке эффект бежал бы вхолостую. `owner` —
    // значение рефа, оно неизменно.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, rect?.x, rect?.y, rect?.width, rect?.height]);

  // Снятие — отдельным эффектом без зависимостей: иначе смена `rect` успевала
  // бы стереть только что объявленную зону на каждом обновлении.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => clearReservedZone(id, owner), [id]);
}

/**
 * Высота объявленной зоны — для вёрстки, которая должна под неё подвинуться.
 *
 * Шапка на вебе `position: fixed`, поэтому под ней ничего не резервируется само
 * — компенсировать её высоту обязана страница. До сих пор каждая делала это
 * своей константой (90 на десктопе, 58 на мобильном, 80 у прокрученной, 56 в
 * комнате), и константы разошлись: высота живёт в `Header.tsx`, а её слепки —
 * в четырёх других файлах. Отсюда «первое сообщение уходит под верхний слой»:
 * отступ равен не фактической высоте, а тому, чему она когда-то равнялась.
 *
 * `fallback` — то самое число, что стояло раньше: до первого `onLayout` (и на
 * экранах, где шапки нет вовсе) вёрстка не должна дёргаться.
 */
export function useReservedZoneHeight(id: string, fallback: number): number {
  const [height, setHeight] = useState(() => getReservedZone(id)?.height ?? fallback);
  useEffect(() => subscribeToReservedZones(() => {
    setHeight(getReservedZone(id)?.height ?? fallback);
  }), [id, fallback]);
  return height;
}
